import { fmtInt } from '../../utils/statsFormat';
import { useChartTooltip, useElementWidth } from './useChartLayout';
import ChartTooltip from './ChartTooltip';
import ScaleLegend, { seqOpacity } from './ScaleLegend';
import './charts.css';

/**
 * Coverage by bearing and distance.
 *
 * Extracted from Statistics.js PolarCoverage, with three changes: the colour
 * comes from the --chart-seq token instead of a hard-coded hex (it was the last
 * literal on the page), the ring labels carry distances so the plot is readable
 * without hovering, and it ships a scale legend because opacity alone is a
 * colour-only encoding.
 */
function PolarChart({ heatmap, unitLabel = 'km' }) {
  const [wrapRef, width] = useElementWidth(420);
  const { tip, show, hide } = useChartTooltip();

  const sectors = (heatmap && heatmap.sectors) || [];
  const hasData = sectors.some(s => (s.positionCount || 0) > 0);
  if (!hasData) return <div className="chart-card-empty">No coverage data</div>;

  // Square, capped so it does not balloon when it spans two grid tracks.
  const size = Math.max(240, Math.min(width, 460));
  const cx = size / 2;
  const cy = size / 2;
  const maxR = size / 2 - 26; // room for the compass letters

  const bucket = (heatmap && heatmap.distanceSectorWidth) || 50;
  const maxDist = Math.max(bucket, ...sectors.map(s => (s.distanceStart || 0) + bucket));
  const maxCount = Math.max(1, (heatmap && heatmap.maxPositionsInSector) || 0,
    ...sectors.map(s => s.positionCount || 0));

  const rOf = d => (d / maxDist) * maxR;
  const pt = (bearing, r) => {
    const a = (bearing - 90) * Math.PI / 180; // 0° = N, clockwise
    return [cx + r * Math.cos(a), cy + r * Math.sin(a)];
  };

  const rings = [];
  for (let d = bucket; d <= maxDist + 0.001; d += bucket) rings.push(d);
  const compass = [['N', 0], ['E', 90], ['S', 180], ['W', 270]];

  const onEnter = (s, evt) => {
    const rect = wrapRef.current?.getBoundingClientRect();
    const from = Math.round(s.distanceStart || 0);
    const to = Math.round((s.distanceStart || 0) + bucket);
    show({
      x: rect ? evt.clientX - rect.left : cx,
      y: rect ? evt.clientY - rect.top : cy,
      title: `${s.compassDirection || (s.bearingStart || 0) + '°'} · ${from}–${to} ${unitLabel}`,
      rows: [{ value: fmtInt(s.positionCount), name: 'positions' }],
    });
  };

  return (
    <div className="chart-plot chart-plot-center" ref={wrapRef}>
      <svg className="chart-svg" width={size} height={size}
           viewBox={`0 0 ${size} ${size}`} role="img"
           onPointerLeave={hide}>
        {rings.map((d, i) => (
          <circle key={`r${i}`} cx={cx} cy={cy} r={rOf(d)} fill="none"
                  className="chart-grid-line" />
        ))}
        {[0, 45, 90, 135].map(b => {
          const [x1, y1] = pt(b, maxR);
          const [x2, y2] = pt(b + 180, maxR);
          return <line key={`sp${b}`} x1={x1} y1={y1} x2={x2} y2={y2}
                       className="chart-grid-line" />;
        })}

        {sectors.filter(s => (s.positionCount || 0) > 0).map((s, i) => {
          const b0 = s.bearingStart || 0;
          const b1 = s.bearingEnd != null ? s.bearingEnd : b0 + 15;
          const ri = rOf(s.distanceStart || 0);
          const ro = rOf((s.distanceStart || 0) + bucket);
          const [x0i, y0i] = pt(b0, ri);
          const [x0o, y0o] = pt(b0, ro);
          const [x1o, y1o] = pt(b1, ro);
          const [x1i, y1i] = pt(b1, ri);
          const path = ri <= 0.01
            ? `M${cx} ${cy} L${x0o} ${y0o} A${ro} ${ro} 0 0 1 ${x1o} ${y1o} Z`
            : `M${x0i} ${y0i} L${x0o} ${y0o} A${ro} ${ro} 0 0 1 ${x1o} ${y1o} `
              + `L${x1i} ${y1i} A${ri} ${ri} 0 0 0 ${x0i} ${y0i} Z`;
          return (
            <path key={`s${i}`} d={path} fill="var(--chart-seq)"
                  fillOpacity={seqOpacity((s.positionCount || 0) / maxCount)}
                  className="chart-sector"
                  tabIndex={0}
                  role="button"
                  aria-label={`${s.compassDirection || b0 + '°'}, `
                    + `${Math.round(s.distanceStart || 0)} to `
                    + `${Math.round((s.distanceStart || 0) + bucket)} ${unitLabel}, `
                    + `${fmtInt(s.positionCount)} positions`}
                  onPointerEnter={(e) => onEnter(s, e)}
                  onPointerMove={(e) => onEnter(s, e)}
                  onFocus={(e) => onEnter(s, e)}
                  onBlur={hide} />
          );
        })}

        {/* Distance rings labelled, so range is readable without hovering. */}
        {rings.map((d, i) => (
          i % Math.max(1, Math.round(rings.length / 3)) === 0 || i === rings.length - 1 ? (
            <text key={`rl${i}`} x={cx + 3} y={cy - rOf(d) + 11}
                  className="chart-axis-label">{Math.round(d)}</text>
          ) : null
        ))}

        {compass.map(([label, b]) => {
          const [x, y] = pt(b, maxR + 15);
          return <text key={label} x={x} y={y + 5} className="chart-compass-label"
                       textAnchor="middle">{label}</text>;
        })}
      </svg>

      <ScaleLegend max={maxCount} label={`positions · rings ${Math.round(bucket)} ${unitLabel}`} />
      <ChartTooltip tip={tip} containerWidth={width} />
    </div>
  );
}

export default PolarChart;
